import { GlobeIcon } from "@images/svgIcons/globeIcon"
import { InfinityIcon } from "@images/svgIcons/infinityIcon"
import { LockIcon } from "@images/svgIcons/lockIcon"
import { OnOffIcon } from "@images/svgIcons/onOffIcon"
import { SubscribersIcon } from "@images/svgIcons/subscribersIcon"
import { word } from "@modules/core/utils/i18n"
import { Path } from "@modules/navigation/routes"
import { useMySpaceNavigate } from "@modules/navigation/useMySpaceNavigate"
import { ProfileAvatar } from "@modules/profile/common/components/profileAvatar"
import { useProgramRights } from "@modules/programs/hooks/useProgramRights"
import { useProgramSidebar } from "@modules/programs/hooks/useProgramSidebar"
import { ProgramInfinite, ProgramInfiniteTypeOf } from "@modules/programs/programInfiniteTypes"
import { ProgramOnOff, ProgramOnOffStatus, ProgramOnOffTypeOf } from "@modules/programs/programOnOffTypes"
import { ProgramVisibility } from "@modules/programs/programTypes"
import {
  getDisplayStatus,
  getDisplayVisibility,
  getDurationText,
  getSubscriptionCountText,
} from "@modules/programs/programUtils"
import { HUButton } from "@modules/ui/components/huButton"
import { HUNavigateButton } from "@modules/ui/components/huNavigateButton"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { HURow, HUStack } from "@modules/ui/components/layout"
import styled, { useTheme } from "styled-components"

export const ProgramInformation: React.FC<{
  program: ProgramInfinite | ProgramOnOff
}> = ({ program }) => {
  const theme = useTheme()
  const { isAuthor, isAcceptedSharedCoach } = useProgramRights(program)
  const programSubscribersText = getSubscriptionCountText(
    program.subscriptionCount,
    isAuthor,
    isAcceptedSharedCoach,
    true,
    program._programType,
  )
  const programDurationText = "duration" in program && getDurationText(program.duration)
  const mySpaceNavigate = useMySpaceNavigate()
  const { navigateToProgramView } = useProgramSidebar()
  const toast = useToast()
  const navigate = useMySpaceNavigate()
  const onViewDetailsProgramClick = () => {
    navigateToProgramView(program.id, program._programType, true)
  }

  const canUserChatWithMembers =
    program && program._programType !== ProgramOnOffTypeOf && (isAuthor || isAcceptedSharedCoach || program.subscribed)

  const openChatWithProgramGroup = () => {
    if (canUserChatWithMembers) {
      if (program.id) {
        navigate(Path.Chat.Root, { state: { programToChat: program.id } })
      }
    }
  }

  const canUserShareInvitationLink =
    program &&
    program.invitationLink &&
    (program._programType === ProgramInfiniteTypeOf || program.status === ProgramOnOffStatus.PUBLISHED) &&
    (program.visibility === ProgramVisibility.PUBLIC ||
      (program.visibility === ProgramVisibility.PRIVATE && (isAuthor || isAcceptedSharedCoach)))

  const onCopyInvitationLink = () => {
    if (canUserShareInvitationLink) {
      navigator.clipboard.writeText(program.invitationLink)
      toast.show({ severity: "info", detail: word("program.details.share.success") })
    }
  }
  return (
    <>
      <HUStack className="pl-3 pr-3 col-6" gap={2}>
        <HURow className="align-items-center gap-2">
          {program._programType === ProgramInfiniteTypeOf ? (
            <InfinityIcon color={theme.mainWhite} width={24} height={24} />
          ) : (
            <OnOffIcon color={theme.mainWhite} width={24} height={24} />
          )}
          <HUText color={theme.mainWhite} fontStyle="TL" numberOfLine={1}>
            {program.title}
          </HUText>
        </HURow>

        <div className="flex align-items-center">
          {program.visibility === ProgramVisibility.PUBLIC ? (
            <GlobeIcon color={theme.primaryColor} width={18} height={18} />
          ) : (
            <LockIcon color={theme.secondaryColor200} width={18} height={18} />
          )}
          <HUText color={theme.secondaryColor200} fontStyle="BM" className="ml-2">
            {getDisplayVisibility(program.visibility)}{" "}
            {program._programType === ProgramOnOffTypeOf &&
              program.status === ProgramOnOffStatus.DRAFT &&
              `(${getDisplayStatus(program.status).toLowerCase()})`}
          </HUText>
        </div>

        {program._programType === ProgramOnOffTypeOf && (
          <div className="flex align-items-center">
            <span className="pi pi-calendar" style={{ color: theme.secondaryColor200 }} />
            <HUText color={theme.secondaryColor200} fontStyle="BM" className="ml-2">
              {programDurationText}
            </HUText>
          </div>
        )}

        <HURow className="align-items-center">
          <SubscribersIcon color={theme.secondaryColor200} width={18} height={18} />
          <HUText color={theme.secondaryColor200} fontStyle="BM" className="ml-2">
            {programSubscribersText}
          </HUText>
        </HURow>

        <HURow className="align-items-center">
          <i
            style={{ fontSize: "1rem", color: theme.secondaryColor200 }}
            className={`${program.defaultHideSessionContent ? "pi pi-eye-slash" : "pi pi-eye"}`}
          />
          <HUText color={theme.secondaryColor200} fontStyle="BM" className="ml-2">
            {word("program.details.hideSessionContent", {
              contentHidden: program.defaultHideSessionContent
                ? word("program.details.sessionContent.hidden")
                : word("program.details.sessionContent.apparent"),
            })}
          </HUText>
        </HURow>

        <ReturnToTrainingButton
          label={word("program.returnToTraining")}
          link
          to={Path.MySpace.Training}
          onPress={() => mySpaceNavigate(Path.MySpace.Training)}
          type="button"
          icon={{
            iconView: <span className={`p-button-icon p-c pi pi-angle-left`} />,
          }}
          iconPosition="left"
          color={theme.secondaryColor200}
        />
      </HUStack>
      <HUStack
        className="pl-3 col-6 justify-content-between"
        gap={10}
        style={{ borderLeft: "1px solid " + theme.secondaryColor300 }}
      >
        <HURow className="align-items-center gap-2">
          <ProfileAvatar profile={{ avatar: program.author.avatar, username: program.author.firstname }} size={46} />
          <HUStack gap={0} className="text-align-left overflow-hidden">
            <HUText
              fontStyle="LL"
              color={theme.mainWhite}
              className="overflow-hidden text-overflow-ellipsis"
            >{`${program.author.firstname} ${program.author.lastname}`}</HUText>
            <HUText fontStyle="BS" color={theme.secondaryColor200}>
              {word("program.label.author")}
            </HUText>
          </HUStack>
        </HURow>
        <HURow className="align-items-center justify-content-end gap-2">
          {canUserChatWithMembers && (
            <HUButton
              type="Rounded"
              colorType="Secondary"
              size="XS"
              onClick={openChatWithProgramGroup}
              icon={{
                iconView: <span className="pi pi-comments" />,
              }}
              isTooltipDisabled={false}
              tooltipId={"chat-with-members"}
              tooltipContent={word("chat.button.goToProgramChat")}
            />
          )}
          {canUserShareInvitationLink && (
            <HUButton
              type="Rounded"
              colorType="Secondary"
              size="XS"
              onClick={onCopyInvitationLink}
              icon={{
                iconView: <span className="pi pi-upload" />,
              }}
              isTooltipDisabled={false}
              tooltipId={"dynamic-link"}
              tooltipContent={word("sidebar.button.dynamicLink")}
            />
          )}
          {/* Do not touch : Not working on a ternary : bug with tooltip */}
          {(isAuthor || isAcceptedSharedCoach) && (
            <HUButton
              type="Rounded"
              colorType="Primary"
              size="XS"
              onClick={onViewDetailsProgramClick}
              icon={{
                iconView: <span className="pi pi-cog" />,
              }}
              isTooltipDisabled={false}
              tooltipId={"tooltip-settings"}
              tooltipContent={word("tooltip.settings")}
            />
          )}
          {!(isAuthor || isAcceptedSharedCoach) && (
            <HUButton
              text={word("program.seeInformation")}
              onClick={onViewDetailsProgramClick}
              type="Default"
              size="M"
              colorType="Quaternary"
              textColor={theme.mainWhite}
              className="pt-2 pb-2 pl-3 pr-3 align-self-end"
            />
          )}
        </HURow>
      </HUStack>
    </>
  )
}

const ReturnToTrainingButton = styled(HUNavigateButton)`
  max-width: fit-content;
  margin-top: auto;

  & > div {
    border-radius: 0;
    padding: 0 !important;
  }
`
